import React, {
	useRef,
	useState,
	useEffect,
	useCallback,
	forwardRef,
	useImperativeHandle,
} from "react";
import { Stage, Layer, Rect, Text, Group, Image as KImage, Transformer } from "react-konva";
import { CANVAS_W, CANVAS_H } from "../../data/scrapbook/config";

/* ── Image loader ── */
function useLoadedImage(src) {
	const [img, setImg] = useState(null);
	useEffect(() => {
		if (!src) { setImg(null); return; }
		const image = new window.Image();
		image.crossOrigin = "anonymous";
		image.onload = () => setImg(image);
		image.onerror = () => setImg(null);
		image.src = src;
	}, [src]);
	return img;
}

/* ── Cover-fit helper ── */
function coverFit(imgW, imgH, slotW, slotH) {
	const imgR = imgW / imgH;
	const slotR = slotW / slotH;
	let w, h;
	if (imgR > slotR) { h = slotH; w = slotH * imgR; }
	else { w = slotW; h = slotW / imgR; }
	return { x: -(w - slotW) / 2, y: -(h - slotH) / 2, width: w, height: h };
}

/* ── Photo frame metrics (same white margin on all sides) ── */
function getFrameMetrics(w, h) {
	const pad = 22;
	const frameW = w + pad * 2;
	const frameH = h + pad * 2;
	const photoX = -w / 2;
	const photoY = -h / 2;
	return { pad, frameW, frameH, photoX, photoY };
}

/* ═══════════════════════════════════════════════
   DraggablePhoto — empty or filled, always movable
   ═══════════════════════════════════════════════ */
function DraggablePhoto({ photo, onDragEnd, onSelect }) {
	const img = useLoadedImage(photo.src || null);
	const w = photo.width;
	const h = photo.height;
	const { frameW, frameH, photoX, photoY } = getFrameMetrics(w, h);
	const fit = img ? coverFit(img.width, img.height, w, h) : null;

	return (
		<Group
			id={photo.id}
			x={photo.x}
			y={photo.y}
			rotation={photo.rotation || 0}
			scaleX={photo.scaleX || 1}
			scaleY={photo.scaleY || 1}
			draggable
			onDragEnd={(e) => onDragEnd(photo.id, e.target.x(), e.target.y())}
			onClick={() => onSelect(photo.id)}
			onTap={() => onSelect(photo.id)}
		>
			<Rect
				x={-frameW / 2} y={-frameH / 2}
				width={frameW} height={frameH}
				fill="#FFFFFF" cornerRadius={4}
				shadowColor="rgba(0,0,0,0.14)"
				shadowBlur={14} shadowOffsetY={5}
			/>
			{img && fit ? (
				<Group clipX={photoX} clipY={photoY} clipWidth={w} clipHeight={h}>
					<KImage
						image={img}
						x={photoX + fit.x}
						y={photoY + fit.y}
						width={fit.width} height={fit.height}
					/>
				</Group>
			) : (
				<Group listening={false}>
					<Rect
						x={photoX} y={photoY}
						width={w} height={h}
						fill="#F0EEEA"
						stroke="rgba(0,0,0,0.12)" strokeWidth={2} dash={[12, 8]}
						cornerRadius={2}
					/>
					<Text
						text="+" x={photoX} y={photoY}
						width={w} height={h - 30}
						align="center" verticalAlign="middle"
						fontSize={54} fill="rgba(0,0,0,0.22)" fontFamily="Poppins"
					/>
					<Text
						text="Añade tu foto" x={photoX}
						y={photoY + h - 50}
						width={w} height={40}
						align="center" verticalAlign="middle"
						fontSize={22} fill="rgba(0,0,0,0.35)" fontFamily="Playfair Display"
					/>
				</Group>
			)}
			{photo.frame === "tape" && (
				<Rect
					x={-40} y={-frameH / 2 - 10}
					width={80} height={28}
					fill="rgba(255,220,120,0.6)"
					rotation={photo.tapeAngle || 3}
					cornerRadius={2}
				/>
			)}
		</Group>
	);
}

/* ═══════════════════════════════════════════════
   DraggableText — editable text that can move/rotate
   ═══════════════════════════════════════════════ */
function DraggableText({ item, bg, onDragEnd, onSelect }) {
	const display = item.value || item.placeholder || "Escribe aquí...";
	const w = item.width || 800;
	const fs = item.fontSize || 36;

	return (
		<Group
			id={item.id}
			x={item.x}
			y={item.y}
			rotation={item.rotation || 0}
			scaleX={item.scaleX || 1}
			scaleY={item.scaleY || 1}
			draggable
			onDragEnd={(e) => onDragEnd(item.id, e.target.x(), e.target.y())}
			onClick={() => onSelect(item.id)}
			onTap={() => onSelect(item.id)}
		>
			<Text
				text={display}
				x={-w / 2}
				y={-fs / 2}
				width={w}
				align={item.align || "center"}
				fontFamily={item.fontFamily || "Playfair Display"}
				fontStyle={item.fontStyle || "normal"}
				fontSize={fs}
				fill={item.color || bg.text || "#1a1a2e"}
				lineHeight={1.35}
			/>
		</Group>
	);
}

/* ── Template decorations can reference the background palette ── */
function resolveDecoColor(color, bg) {
	if (color === "accent") return bg?.accent || "#c4956a";
	if (color === "soft") return bg?.textSoft || "rgba(0,0,0,0.3)";
	if (color === "text") return bg?.text || "#1a1a2e";
	return color || bg?.textSoft || "rgba(0,0,0,0.3)";
}

/* ═══════════════════════════════════════════════
   WashiTape — decorative strip or tape image (template)
   ═══════════════════════════════════════════════ */
function WashiTape({ deco, bg }) {
	const img = useLoadedImage(deco.image || null);
	const w = deco.width || 320;

	if (deco.image) {
		if (!img) return null;
		const h = w * (img.height / img.width);
		return (
			<KImage
				image={img}
				x={deco.x} y={deco.y}
				offsetX={w / 2} offsetY={h / 2}
				width={w} height={h}
				rotation={deco.rotation || 0}
				opacity={deco.opacity ?? 0.95}
				listening={false}
			/>
		);
	}

	const h = deco.height || 40;
	return (
		<Rect
			x={deco.x} y={deco.y}
			offsetX={w / 2} offsetY={h / 2}
			width={w} height={h}
			fill={resolveDecoColor(deco.color, bg)}
			rotation={deco.rotation || 0}
			cornerRadius={deco.cornerRadius ?? 2}
			opacity={deco.opacity ?? 0.9}
			listening={false}
		/>
	);
}

/* ═══════════════════════════════════════════════
   DecoText — non-interactive label baked into the layout
   ═══════════════════════════════════════════════ */
function DecoText({ deco, bg }) {
	const fs = deco.fontSize || 32;
	const w = deco.width || fs * 2.5;
	return (
		<Text
			text={deco.content}
			x={deco.x - w / 2}
			y={deco.y - fs / 2}
			width={w}
			align={deco.align || "center"}
			fontFamily={deco.fontFamily || "Playfair Display"}
			fontStyle={deco.fontStyle || "normal"}
			fontSize={fs}
			fill={resolveDecoColor(deco.color, bg)}
			rotation={deco.rotation || 0}
			opacity={deco.opacity ?? 1}
			listening={false}
		/>
	);
}

/* ═══════════════════════════════════════════════
   DraggableSticker — wrapped in Group for Transformer
   ═══════════════════════════════════════════════ */
function DraggableSticker({ sticker, onDragEnd, onSelect }) {
	const img = useLoadedImage(sticker.imageSrc || null);

	const groupProps = {
		id: sticker.id,
		x: sticker.x,
		y: sticker.y,
		rotation: sticker.rotation || 0,
		scaleX: sticker.scaleX || 1,
		scaleY: sticker.scaleY || 1,
		draggable: true,
		onDragEnd: (e) => onDragEnd(sticker.id, e.target.x(), e.target.y()),
		onClick: () => onSelect(sticker.id),
		onTap: () => onSelect(sticker.id),
	};

	if (sticker.type === "washi") {
		const tapeW = sticker.width || 320;
		if (sticker.imageSrc) {
			if (!img) return null;
			const tapeH = tapeW * (img.height / img.width);
			return (
				<Group {...groupProps}>
					<Rect
						x={-tapeW / 2 - 8} y={-tapeH / 2 - 12}
						width={tapeW + 16} height={tapeH + 24}
						fill="transparent"
					/>
					<KImage
						image={img}
						x={-tapeW / 2} y={-tapeH / 2}
						width={tapeW} height={tapeH}
						shadowColor="rgba(0,0,0,0.12)" shadowBlur={8} shadowOffsetY={3}
					/>
				</Group>
			);
		}
		const tapeH = sticker.height || 44;
		return (
			<Group {...groupProps}>
				{/* Invisible hit area so it's easier to select */}
				<Rect
					x={-tapeW / 2 - 8} y={-tapeH / 2 - 12}
					width={tapeW + 16} height={tapeH + 24}
					fill="transparent"
				/>
				<Rect
					x={-tapeW / 2} y={-tapeH / 2}
					width={tapeW} height={tapeH}
					fill={sticker.color}
					cornerRadius={2} opacity={0.85}
					shadowColor="rgba(0,0,0,0.1)" shadowBlur={6} shadowOffsetY={3}
				/>
			</Group>
		);
	}

	if (sticker.imageSrc) {
		if (!img) return null;
		const size = 210;
		const h = size * (img.height / img.width);
		return (
			<Group {...groupProps}>
				<KImage
					image={img}
					x={-size / 2} y={-h / 2}
					width={size} height={h}
					shadowColor="rgba(0,0,0,0.15)" shadowBlur={10} shadowOffsetY={4}
				/>
			</Group>
		);
	}

	const fs = sticker.fontSize || 52;
	return (
		<Group {...groupProps}>
			<Text
				text={sticker.content}
				x={-fs / 2} y={-fs / 2}
				fontSize={fs}
			/>
		</Group>
	);
}

/* ═══════════════════════════════════════════════
   CoverBackground — full-page cover image
   ═══════════════════════════════════════════════ */
function CoverBackground({ src }) {
	const img = useLoadedImage(src);
	if (!img) return null;
	const fit = coverFit(img.width, img.height, CANVAS_W, CANVAS_H);
	return (
		<Group clipX={0} clipY={0} clipWidth={CANVAS_W} clipHeight={CANVAS_H}>
			<KImage
				image={img}
				x={fit.x} y={fit.y}
				width={fit.width} height={fit.height}
				listening={false}
			/>
		</Group>
	);
}

/* ═══════════════════════════════════════════════
   Main Canvas
   ═══════════════════════════════════════════════ */
const ScrapbookCanvas = forwardRef(function ScrapbookCanvas(
	{
		template, background, coverImage, photos, texts, stickers,
		onPhotoDragEnd, onRemovePhoto, onPhotoTransform, onPhotoSelect,
		onTextDragEnd, onTextTransform, onRemoveText, onTextSelect,
		onStickerDragEnd, onRemoveSticker, onStickerTransform,
		selectId,
	},
	ref
) {
	const stageRef = useRef(null);
	const containerRef = useRef(null);
	const transformerRef = useRef(null);
	const [containerSize, setContainerSize] = useState({ w: 400, h: 711 });
	const [selectedId, setSelectedId] = useState(null);
	const [deletePos, setDeletePos] = useState(null);

	useImperativeHandle(ref, () => ({
		getStage: () => stageRef.current,
	}));

	useEffect(() => {
		if (selectId) setSelectedId(selectId);
	}, [selectId]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const measure = () => {
			const w = el.clientWidth;
			const h = el.clientHeight;
			if (w <= 0) return;
			const flexGrow = parseFloat(window.getComputedStyle(el).flexGrow) || 0;
			setContainerSize({
				w,
				h: flexGrow > 0 && h > 0 ? h : w * (CANVAS_H / CANVAS_W),
			});
		};
		measure();
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		if (ro) ro.observe(el);
		window.addEventListener("resize", measure);
		return () => {
			if (ro) ro.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, []);

	const updateDeletePos = useCallback(() => {
		if (!selectedId || !stageRef.current || !containerRef.current) {
			setDeletePos(null);
			return;
		}
		const node = stageRef.current.findOne(`#${selectedId}`);
		if (!node) {
			setDeletePos(null);
			return;
		}
		const abs = node.getClientRect();
		const wrap = containerRef.current.getBoundingClientRect();
		const btnW = 96;
		const btnH = 34;
		const top = Math.max(8, Math.min(wrap.height - btnH - 8, abs.y - wrap.y - btnH - 8));
		const left = Math.max(
			8,
			Math.min(wrap.width - btnW - 8, abs.x - wrap.x + abs.width / 2 - btnW / 2)
		);
		setDeletePos({ top, left });
	}, [selectedId]);

	/* Attach Transformer to selected node */
	useEffect(() => {
		const tr = transformerRef.current;
		if (!tr) return;
		if (selectedId) {
			const stage = stageRef.current;
			const node = stage.findOne(`#${selectedId}`);
			if (node) {
				tr.nodes([node]);
			} else {
				tr.nodes([]);
				setSelectedId(null);
			}
		} else {
			tr.nodes([]);
		}
		tr.getLayer()?.batchDraw();
		requestAnimationFrame(updateDeletePos);
	}, [selectedId, photos, stickers, texts, updateDeletePos]);

	/* Deselect on empty click */
	const handleStageMouseDown = (e) => {
		if (!e.target.draggable()) {
			setSelectedId(null);
			setDeletePos(null);
		}
	};

	/* Handle transform end — read rotation + scale from node */
	const handleTransformEnd = (e) => {
		const node = e.target;
		const id = node.id();
		const attrs = {
			x: node.x(),
			y: node.y(),
			rotation: node.rotation(),
			scaleX: node.scaleX(),
			scaleY: node.scaleY(),
		};

		if (photos.some((p) => p.id === id)) {
			if (onPhotoTransform) onPhotoTransform(id, attrs);
		} else if (texts.some((t) => t.id === id)) {
			/* Bake scale into fontSize so handles stay usable */
			const avgScale = (Math.abs(node.scaleX()) + Math.abs(node.scaleY())) / 2;
			node.scaleX(1);
			node.scaleY(1);
			if (onTextTransform) {
				onTextTransform(id, {
					...attrs,
					scaleX: avgScale,
					scaleY: avgScale,
				});
			}
		} else if (stickers.some((s) => s.id === id)) {
			if (onStickerTransform) onStickerTransform(id, attrs);
		}
		requestAnimationFrame(updateDeletePos);
	};

	const handleSelect = (id) => {
		setSelectedId(id);
		if (texts.some((t) => t.id === id) && onTextSelect) {
			onTextSelect(id);
		}
		if (photos.some((p) => p.id === id) && onPhotoSelect) {
			onPhotoSelect(id);
		}
	};

	const deleteSelected = useCallback(() => {
		if (!selectedId) return;
		if (photos.some((p) => p.id === selectedId)) {
			if (onRemovePhoto) onRemovePhoto(selectedId);
		} else if (texts.some((t) => t.id === selectedId)) {
			if (onRemoveText) onRemoveText(selectedId);
		} else if (stickers.some((s) => s.id === selectedId)) {
			if (onRemoveSticker) onRemoveSticker(selectedId);
		}
		setSelectedId(null);
		setDeletePos(null);
	}, [selectedId, photos, texts, stickers, onRemovePhoto, onRemoveText, onRemoveSticker]);

	useEffect(() => {
		const onKeyDown = (e) => {
			if (!selectedId) return;
			if (e.key !== "Delete" && e.key !== "Backspace") return;
			const tag = e.target?.tagName?.toLowerCase();
			if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
			e.preventDefault();
			deleteSelected();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [selectedId, deleteSelected]);

	const scale = Math.min(containerSize.w / CANVAS_W, containerSize.h / CANVAS_H);
	const stageW = Math.round(CANVAS_W * scale);
	const stageH = Math.round(CANVAS_H * scale);
	const selectedSticker = stickers.find((s) => s.id === selectedId);
	const isColorWashi = selectedSticker?.type === "washi" && !selectedSticker?.imageSrc;

	return (
		<div ref={containerRef} className="sb-canvas-wrapper">
			{selectedId && deletePos && (
				<button
					type="button"
					className="sb-delete-btn"
					style={{ top: deletePos.top, left: deletePos.left }}
					onClick={deleteSelected}
					title="Eliminar (Supr)"
				>
					Eliminar
				</button>
			)}
			<Stage
				ref={stageRef}
				width={stageW}
				height={stageH}
				scaleX={scale} scaleY={scale}
				style={{ borderRadius: 12, overflow: "hidden" }}
				onMouseDown={handleStageMouseDown}
				onTouchStart={handleStageMouseDown}
				onDragMove={updateDeletePos}
				onDragEnd={updateDeletePos}
			>
				{/* Base layer */}
				<Layer>
					<Rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill={background.color} />
					{(coverImage || background.image) && (
						<CoverBackground src={coverImage || background.image} />
					)}

					{template.decorations.map((deco, i) => {
						if (deco.type === "washi") return <WashiTape key={`d-${i}`} deco={deco} bg={background} />;
						if (deco.type === "text") return <DecoText key={`d-${i}`} deco={deco} bg={background} />;
						return null;
					})}
				</Layer>

				{/* Top layer: draggable elements + Transformer */}
				<Layer>
					{photos.map((p) => (
						<DraggablePhoto
							key={p.id} photo={p}
							onDragEnd={onPhotoDragEnd}
							onSelect={handleSelect}
						/>
					))}

					{stickers.map((s) => (
						<DraggableSticker
							key={s.id} sticker={s}
							onDragEnd={onStickerDragEnd}
							onSelect={handleSelect}
						/>
					))}

					{texts.map((t) => (
						<DraggableText
							key={t.id} item={t} bg={background}
							onDragEnd={onTextDragEnd}
							onSelect={handleSelect}
						/>
					))}

					{/* Transformer — rotation + scale handles */}
					<Transformer
						ref={transformerRef}
						rotateEnabled={true}
						resizeEnabled={true}
						keepRatio={!isColorWashi}
						enabledAnchors={
							isColorWashi
								? [
										"middle-left",
										"middle-right",
										"top-center",
										"bottom-center",
										"top-left",
										"top-right",
										"bottom-left",
										"bottom-right",
								  ]
								: ["top-left", "top-right", "bottom-left", "bottom-right"]
						}
						borderStroke="#6366f1"
						borderStrokeWidth={2}
						anchorFill="#fff"
						anchorStroke="#6366f1"
						anchorStrokeWidth={2.5}
						anchorSize={22}
						anchorCornerRadius={11}
						rotateAnchorOffset={40}
						padding={8}
						onTransform={updateDeletePos}
						onTransformEnd={handleTransformEnd}
						boundBoxFunc={(oldBox, newBox) => {
							if (Math.abs(newBox.width) < 20 || Math.abs(newBox.height) < 8) return oldBox;
							return newBox;
						}}
					/>

					{/* Brand watermark — always on top, readable on busy backgrounds */}
					<Group listening={false}>
						<Rect
							x={CANVAS_W / 2 - 220}
							y={CANVAS_H - 88}
							width={440}
							height={44}
							cornerRadius={22}
							fill="rgba(255,255,255,0.72)"
						/>
						<Text
							text="Creado con Viajes Molones"
							x={0}
							y={CANVAS_H - 78}
							width={CANVAS_W}
							align="center"
							fontFamily="Poppins"
							fontSize={22}
							fontStyle="600"
							fill="rgba(45, 35, 25, 0.78)"
						/>
					</Group>
				</Layer>
			</Stage>
		</div>
	);
});

export default ScrapbookCanvas;
